import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CommunityMediaItem,
  CommunityPost,
  CommunityPostCreatePayload,
  CommunityTopic,
  PrescriptionSummary,
} from '../types';
import { fetchCommunityPost, uploadCommunityMedia } from '../services/communityService';
import { fetchPrescriptions } from '../services/profileService';
import { API_BASE_URL } from '../services/apiClient';
import './CommunityComposer.css';

interface CommunityComposerProps {
  topics: CommunityTopic[];
  onSubmit: (payload: CommunityPostCreatePayload) => Promise<CommunityPost>;
  submitting: boolean;
  error: string | null;
  onClearError?: () => void;
  defaultTopic?: string | null;
  onPostCreated?: (post: CommunityPost) => void;
  enableDrafts?: boolean;
  initialRepostId?: string | null;
  onRepostCleared?: () => void;
}

interface RepostPreview {
  post_id: string;
  title: string;
  excerpt?: string | null;
  author_name?: string | null;
  author_username?: string | null;
  author_avatar?: string | null;
}

const DRAFT_STORAGE_KEY = 'community-composer-draft';
const HASHTAG_REGEX = /#([A-Za-z0-9_\-\u4e00-\u9fa5]{1,32})/g;
const CUSTOM_TOPIC_ALLOWED_PATTERN = /[^A-Za-z0-9_\-\u4e00-\u9fa5]/g;
const CUSTOM_TOPIC_MAX_LENGTH = 32;

const parseTopicCandidate = (raw: string): string | null => {
  if (!raw) {
    return null;
  }
  let candidate = raw.trim();
  candidate = candidate.replace(/^#+/, '');
  candidate = candidate.replace(/\s+/g, '');
  candidate = candidate.replace(CUSTOM_TOPIC_ALLOWED_PATTERN, '');
  if (!candidate) {
    return null;
  }
  if (candidate.length > CUSTOM_TOPIC_MAX_LENGTH) {
    candidate = candidate.slice(0, CUSTOM_TOPIC_MAX_LENGTH);
  }
  if (/^[A-Za-z0-9_-]+$/.test(candidate)) {
    candidate = candidate.toLowerCase();
  }
  return candidate;
};

const extractHashtags = (text: string): string[] => {
  const matches = text.match(HASHTAG_REGEX);
  if (!matches) {
    return [];
  }
  return matches
    .map((item) => parseTopicCandidate(item))
    .filter((item): item is string => Boolean(item));
};

const buildMediaUrl = (url: string | null | undefined): string => {
  if (!url) {
    return '';
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `${base}${normalized}`;
};

const CommunityComposer: React.FC<CommunityComposerProps> = ({
  topics,
  onSubmit,
  submitting,
  error,
  onClearError,
  defaultTopic,
  onPostCreated,
  enableDrafts = true,
  initialRepostId,
  onRepostCleared,
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>(defaultTopic ? [defaultTopic] : []);
  const [mediaItems, setMediaItems] = useState<CommunityMediaItem[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [prescriptions, setPrescriptions] = useState<PrescriptionSummary[]>([]);
  const [prescriptionsError, setPrescriptionsError] = useState<string | null>(null);
  const [prescriptionId, setPrescriptionId] = useState<string>('');
  const [repostLookupStatus, setRepostLookupStatus] = useState<'idle' | 'loading' | 'succeeded' | 'failed'>('idle');
  const [repostError, setRepostError] = useState<string | null>(null);
  const [repostPreview, setRepostPreview] = useState<RepostPreview | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [autoTopicNotice, setAutoTopicNotice] = useState<string | null>(null);

  const draftSerializedRef = useRef('');
  const lastRepostIdRef = useRef<string | null>(null);
  const topicsById = useMemo(() => {
    return topics.reduce<Record<string, CommunityTopic>>((acc, topic) => {
      acc[topic.id] = topic;
      return acc;
    }, {});
  }, [topics]);

  const autoAttachTopics = useCallback(
    (hashtags: string[]) => {
      if (!hashtags.length) {
        return;
      }
      const addedTopics: string[] = [];
      setSelectedTopics((prev) => {
        const next = [...prev];
        const seen = new Set(prev);
        hashtags.forEach((topicId) => {
          if (next.length >= 5 || seen.has(topicId)) {
            return;
          }
          next.push(topicId);
          seen.add(topicId);
          addedTopics.push(topicId);
        });
        return next;
      });
      if (addedTopics.length) {
        const labels = addedTopics.map((id) => `#${topicsById[id]?.label ?? id}`).join(' ');
        setAutoTopicNotice(`已自动关联 ${labels}`);
      }
    },
    [topicsById],
  );

  useEffect(() => {
    let mounted = true;
    fetchPrescriptions({ limit: 50 })
      .then((items) => {
        if (mounted) {
          setPrescriptions(items);
        }
      })
      .catch(() => {
        if (mounted) {
          setPrescriptionsError('处方列表加载失败，可稍后重试');
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!defaultTopic) {
      return;
    }
    setSelectedTopics((prev) => {
      if (prev.includes(defaultTopic)) {
        return prev;
      }
      return [defaultTopic, ...prev].slice(0, 5);
    });
  }, [defaultTopic]);

  useEffect(() => {
    if (!enableDrafts || typeof window === 'undefined') {
      return;
    }
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as {
        title?: string;
        content?: string;
        topics?: string[];
        media?: CommunityMediaItem[];
        prescriptionId?: string;
        repost?: RepostPreview | null;
      };
      if (typeof parsed.title === 'string') {
        setTitle(parsed.title);
      }
      if (typeof parsed.content === 'string') {
        setContent(parsed.content);
        const draftHashtags = extractHashtags(parsed.content);
        if (draftHashtags.length) {
          autoAttachTopics(draftHashtags);
        }
      }
      if (Array.isArray(parsed.topics) && parsed.topics.length) {
        setSelectedTopics(parsed.topics.slice(0, 5));
      }
      if (Array.isArray(parsed.media)) {
        setMediaItems(parsed.media.slice(0, 9));
      }
      if (typeof parsed.prescriptionId === 'string') {
        setPrescriptionId(parsed.prescriptionId);
      }
      if (parsed.repost) {
        setRepostPreview(parsed.repost);
        setRepostLookupStatus('succeeded');
        lastRepostIdRef.current = parsed.repost.post_id;
      }
      draftSerializedRef.current = raw;
      setDraftRestored(true);
    } catch (restoreError) {
      console.warn('[CommunityComposer] failed to restore draft', restoreError);
    }
  }, [enableDrafts, autoAttachTopics]);

  useEffect(() => {
    if (!enableDrafts || typeof window === 'undefined') {
      return;
    }
    const draftPayload = {
      title,
      content,
      topics: selectedTopics,
      media: mediaItems,
      prescriptionId,
      repost: repostPreview,
    };
    const shouldClear =
      !draftPayload.title &&
      !draftPayload.content &&
      draftPayload.topics.length === 0 &&
      draftPayload.media.length === 0 &&
      !draftPayload.prescriptionId &&
      !draftPayload.repost;

    if (shouldClear) {
      if (draftSerializedRef.current) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        draftSerializedRef.current = '';
      }
      return;
    }

    try {
      const serialized = JSON.stringify(draftPayload);
      if (serialized !== draftSerializedRef.current) {
        localStorage.setItem(DRAFT_STORAGE_KEY, serialized);
        draftSerializedRef.current = serialized;
      }
    } catch (persistError) {
      console.warn('[CommunityComposer] failed to persist draft', persistError);
    }
  }, [enableDrafts, title, content, selectedTopics, mediaItems, prescriptionId, repostPreview]);

  useEffect(() => {
    if (!autoTopicNotice || typeof window === 'undefined') {
      return;
    }
    const timer = window.setTimeout(() => setAutoTopicNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [autoTopicNotice]);


  const handleToggleTopic = (topicId: string) => {
    setSelectedTopics((prev) => {
      if (prev.includes(topicId)) {
        return prev.filter((id) => id !== topicId);
      }
      if (prev.length >= 5) {
        return prev;
      }
      return [...prev, topicId];
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !files.length) {
      return;
    }
    const remainingSlots = Math.max(0, 9 - mediaItems.length);
    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    if (!filesToUpload.length) {
      setLocalError('最多可上传 9 个附件');
      return;
    }

    setLocalError(null);
    setUploadingCount((count) => count + filesToUpload.length);
    try {
      const uploaded: CommunityMediaItem[] = [];
      for (const file of filesToUpload) {
        const result = await uploadCommunityMedia(file);
        uploaded.push({
          url: result.url,
          kind: result.kind,
          cover_url: result.cover_url,
          name: result.name || file.name,
          width: result.width,
          height: result.height,
          duration: result.duration,
        });
      }
      setMediaItems((prev) => [...prev, ...uploaded]);
    } catch (uploadError) {
      setLocalError(uploadError instanceof Error ? uploadError.message : '附件上传失败');
    } finally {
      setUploadingCount((count) => Math.max(0, count - filesToUpload.length));
      event.target.value = '';
    }
  };

  const handleRemoveMedia = (url: string) => {
    setMediaItems((prev) => prev.filter((item) => item.url !== url));
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setSelectedTopics(defaultTopic ? [defaultTopic] : []);
    setMediaItems([]);
    setPrescriptionId('');
    setRepostPreview(null);
    setRepostLookupStatus('idle');
    setRepostError(null);
    setAutoTopicNotice(null);
    setDraftRestored(false);
    lastRepostIdRef.current = null;
    if (enableDrafts && typeof window !== 'undefined') {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      draftSerializedRef.current = '';
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: CommunityPostCreatePayload = {
      title: title.trim(),
      content: content.trim(),
      topics: selectedTopics,
      media: mediaItems,
      publish: true,
    };

    if (!payload.title || !payload.content) {
      setLocalError('请完整填写标题和内容');
      return;
    }

    if (prescriptionId) {
      const numericId = Number(prescriptionId);
      if (!Number.isNaN(numericId)) {
        payload.prescription_id = numericId;
      }
    }

    if (repostPreview) {
      payload.repost_of = repostPreview.post_id;
    }

    setLocalError(null);
    try {
      const createdPost = await onSubmit(payload);
      resetForm();
      if (onPostCreated) {
        onPostCreated(createdPost);
      }
    } catch (submitError) {
      setLocalError(submitError instanceof Error ? submitError.message : '发布失败，请稍后重试');
    }
  };

  const handleContentChange = (value: string) => {
    setContent(value);
    if (localError) {
      setLocalError(null);
    }
    if (error && onClearError) {
      onClearError();
    }
    const hashtags = extractHashtags(value);
    if (hashtags.length) {
      autoAttachTopics(hashtags);
    }
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (localError) {
      setLocalError(null);
    }
    if (error && onClearError) {
      onClearError();
    }
  };

  const loadRepostTarget = useCallback(async (rawId: string) => {
    const candidate = rawId.trim();
    if (!candidate) {
      setRepostPreview(null);
      setRepostLookupStatus('idle');
      setRepostError(null);
      lastRepostIdRef.current = null;
      return;
    }
    setRepostLookupStatus('loading');
    setRepostError(null);
    try {
      const original = await fetchCommunityPost(candidate);
      const excerptSource = original.content ?? '';
      const excerpt = excerptSource.length > 140 ? `${excerptSource.slice(0, 140).trim()}…` : excerptSource;
      const preview: RepostPreview = {
        post_id: original.post_id,
        title: original.title,
        excerpt,
        author_name: original.author?.name ?? null,
        author_username: original.author?.username ?? null,
        author_avatar: original.author?.avatar_url ?? null,
      };
      setRepostPreview(preview);
      setRepostLookupStatus('succeeded');
      lastRepostIdRef.current = candidate;
    } catch (lookupError) {
      const message = lookupError instanceof Error
        ? lookupError.message
        : '未能加载原帖，请返回帖子详情页重新发起转发';
      setRepostError(message);
      setRepostLookupStatus('failed');
      setRepostPreview(null);
    }
  }, []);

  useEffect(() => {
    if (!initialRepostId) {
      return;
    }
    const normalized = initialRepostId.trim();
    if (!normalized) {
      return;
    }
    if (repostPreview && repostPreview.post_id === normalized) {
      return;
    }
    if (
      lastRepostIdRef.current === normalized &&
      (repostLookupStatus === 'loading' || repostLookupStatus === 'failed')
    ) {
      return;
    }
    lastRepostIdRef.current = normalized;
    void loadRepostTarget(normalized);
  }, [initialRepostId, loadRepostTarget, repostPreview, repostLookupStatus]);

  const handleClearRepost = () => {
    setRepostPreview(null);
    setRepostError(null);
    setRepostLookupStatus('idle');
    lastRepostIdRef.current = null;
    if (onRepostCleared) {
      onRepostCleared();
    }
  };

  const handleDiscardDraft = () => {
    resetForm();
  };

  return (
    <section className="community-composer">
      <h2>发布社区动态</h2>
      <form className="community-composer__form" onSubmit={handleSubmit}>
        {draftRestored && enableDrafts && (
          <div className="community-composer__draft-banner">
            已恢复上次未完成的草稿。
            <button type="button" onClick={handleDiscardDraft}>
              清空草稿
            </button>
          </div>
        )}
        <input
          type="text"
          placeholder="请输入标题，例如：#节气调养# 初春护阳经验分享"
          value={title}
          onChange={(event) => handleTitleChange(event.target.value)}
          disabled={submitting}
        />

        <textarea
          placeholder="分享你的养生心得、案例经验或疑问..."
          value={content}
          onChange={(event) => handleContentChange(event.target.value)}
          disabled={submitting}
        />

        {autoTopicNotice && (
          <div className="community-composer__auto-topic">{autoTopicNotice}</div>
        )}

        <div className="community-composer__topics">
          <span>话题分类（最多选择5个）：</span>
          <div className="community-composer__topic-list">
            {topics.map((topic) => {
              const selected = selectedTopics.includes(topic.id);
              return (
                <button
                  key={topic.id}
                  type="button"
                  className={`community-composer__topic${selected ? ' community-composer__topic--selected' : ''}`}
                  onClick={() => handleToggleTopic(topic.id)}
                  disabled={submitting}
                >
                  {topic.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="community-composer__media">
          <label className="community-composer__media-upload">
            <input type="file" accept="image/*,video/*" multiple onChange={handleFileChange} disabled={submitting || uploadingCount > 0} />
            <span>{uploadingCount > 0 ? `上传中 (${uploadingCount})...` : '添加图片或视频'}</span>
          </label>
          {mediaItems.length > 0 && (
            <div className="community-composer__media-preview">
              {mediaItems.map((item) => {
                const previewUrl = buildMediaUrl(item.url);
                if (!previewUrl) {
                  return null;
                }
                const previewKey = `${item.url}-${item.kind}`;
                return (
                  <figure key={previewKey}>
                    {item.kind === 'image' ? (
                      <img src={previewUrl} alt={item.name || item.url} />
                    ) : (
                      <video
                        src={previewUrl}
                        controls
                        poster={item.cover_url ? buildMediaUrl(item.cover_url) : undefined}
                      />
                    )}
                    <figcaption>
                      <button type="button" onClick={() => handleRemoveMedia(item.url)} disabled={submitting}>
                        移除
                      </button>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          )}
        </div>

        <div className="community-composer__repost">
          <div className="community-composer__repost-header">
            <span>引用原帖</span>
            {repostPreview && (
              <button
                type="button"
                className="community-composer__repost-clear"
                onClick={handleClearRepost}
                disabled={submitting}
              >
                取消关联
              </button>
            )}
          </div>
          {repostLookupStatus === 'loading' && (
            <div className="community-composer__repost-status">原帖加载中...</div>
          )}
          {repostError && <span className="community-composer__hint">{repostError}</span>}
          {repostPreview ? (
            <div className="community-composer__repost-preview">
              <div className="community-composer__repost-avatar">
                {repostPreview.author_avatar ? (
                  <img src={repostPreview.author_avatar} alt={repostPreview.author_name ?? repostPreview.author_username ?? '原帖作者'} />
                ) : (
                  <span>{(repostPreview.author_name || repostPreview.author_username || '友').slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <div className="community-composer__repost-meta">
                <strong>{repostPreview.title || '转发内容'}</strong>
                <span>{repostPreview.author_name || repostPreview.author_username || '社区用户'}</span>
                {repostPreview.excerpt && <p>{repostPreview.excerpt}</p>}
              </div>
            </div>
          ) : (
            <div className="community-composer__repost-empty">
              当前未引用原帖，可在帖子详情页点击“转发”按钮后自动填充。
            </div>
          )}
        </div>

        <div className="community-composer__prescription">
          <label htmlFor="community-composer-prescription">
            可选：关联个人处方
          </label>
          <select
            id="community-composer-prescription"
            value={prescriptionId}
            onChange={(event) => setPrescriptionId(event.target.value)}
            disabled={submitting}
          >
            <option value="">未关联</option>
            {prescriptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title || `处方 #${item.id}`}
              </option>
            ))}
          </select>
          {prescriptionsError && <span className="community-composer__hint">{prescriptionsError}</span>}
        </div>

        {(localError || error) && <div className="community-composer__error">{localError || error}</div>}

        <div className="community-composer__actions">
          <button type="submit" disabled={submitting || uploadingCount > 0}>
            {submitting ? '发布中...' : '立即发布'}
          </button>
        </div>
      </form>
    </section>
  );
};

export default CommunityComposer;
